import React from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import ValidationLegacyStrings from '@bingads-webui-clientcenter/validation-legacy-strings';
import { Input } from '@bingads-webui-react/primitive-with-validation-and-signal';
import { withError, withValidation } from '@bingads-webui-react/validation';
import { LineByLineFormGroup, InputFormGroup } from '@bingads-webui-cc-react/layout';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { DefaultBusinessNameMinLength, DefaultBusinessNameMaxLength } from './constants';

const { RegGeneralInputBoxRegExp } = Constants;
const WrappedDiv = withError(withValidation(props => <div {...props} />));

export class BusinessName extends React.Component {
  state = { businessName: this.props.businessName };

  componentDidUpdate(prevProps, prevState) {
    if (!_.isEqual(prevProps.businessName, this.props.businessName)) {
      // Below setState use should be allowed, discussion here https://github.com/yannickcr/eslint-plugin-react/issues/1707
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ ...prevState, businessName: this.props.businessName });
    }
  }

  onValidate = (result) => {
    this.setState({ businessName: result.data.businessName, isValid: result.isValid }, () => this.props.onBusinessNameChange(this.state));
  };

  getLegacyI18n = () => buildLegacyI18n(this.props.i18n, ValidationLegacyStrings);

  render() {
    const {
      id, i18n, showLineByLineFormGroup, helpPopupTopic, alwaysShowError, isReadonly, passiveError, isOptional, className,
    } = this.props;
    const legacyI18n = this.getLegacyI18n();

    const label = i18n.getString(_TL_('Legal business name'));

    const customErrors = {
      required: i18n.getString(_TL_('Please enter a business name')),
      minLength: i18n.getString(_TL_('Business name must be 2 characters or more.')),
      maxLength: i18n.getString(_TL_('Business name must be 90 characters or less.')),
      pattern: i18n.getString(_TL_('Please enter a business name')),
    };

    const errorProcessor = errors => _.map(
      errors,
      (oldError, key) => customErrors[key] || oldError
    );

    const CustomizedFormGroup = showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;

    const customValidator = (value, defaultValidator) => {
      const validationMessages = defaultValidator(value);
      if (!_.isEmpty(validationMessages)) {
        return validationMessages;
      }

      return _.isString(value) && value.length > 0 && value.length < DefaultBusinessNameMinLength
        ? [customErrors.minLength] : [];
    };

    return (
      <CustomizedFormGroup
        id={`${id}_BusinessNameFormGroup`}
        className={className}
        label={label}
        helpPopupTopic={helpPopupTopic}
        helpPopupAriaLabel={i18n.getString(_TL_('Legal business name tooltip'))}
      >
        <WrappedDiv validationProps={{ i18n: legacyI18n, ...passiveError.Outer }} >
          <Input
            aria-label={label}
            key="BusinessName"
            name="BusinessName"
            id={`${id}_BusinessName`}
            isStatic={isReadonly}
            staticDisplay={inputValue => inputValue || i18n.getString(_TL_('(Not set)'))}
            validationProps={{
              onValidate: this.onValidate,
              field: 'businessName',
              data: { businessName: this.state.businessName },
              schema: {
                properties: {
                  businessName: {
                    type: 'string',
                    required: !isOptional,
                    maxLength: DefaultBusinessNameMaxLength,
                    pattern: RegGeneralInputBoxRegExp,
                  },
                },
              },
              validator: customValidator,
              skipValidation: isReadonly,
              i18n: legacyI18n,
              errorProcessor,
              alwaysShowError,
              ...passiveError.BusinessName,
            }}
            disabled={this.props.disabled}
          />
        </WrappedDiv>
      </CustomizedFormGroup>
    );
  }
}

const PassiveErrorShape = PropTypes.shape({
  showErrorOnPassiveMessages: PropTypes.bool,
  passiveMessages: PropTypes.arrayOf(PropTypes.string),
});

BusinessName.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  id: PropTypes.string.isRequired,
  businessName: PropTypes.string,
  isReadonly: PropTypes.bool,
  onBusinessNameChange: PropTypes.func.isRequired,
  showLineByLineFormGroup: PropTypes.bool,
  disabled: PropTypes.bool,
  helpPopupTopic: PropTypes.string,
  alwaysShowError: PropTypes.bool,
  passiveError: PropTypes.shape({
    BusinessName: PassiveErrorShape,
    Outer: PassiveErrorShape,
  }),
  isOptional: PropTypes.bool,
  className: PropTypes.string,
};

BusinessName.defaultProps = {
  helpPopupTopic: '',
  businessName: '',
  isReadonly: false,
  showLineByLineFormGroup: false,
  disabled: false,
  alwaysShowError: false,
  passiveError: {},
  isOptional: false,
  className: '',
};
